import { useCallback, useEffect, useMemo } from 'react';
// Types
import type { DateTimePickerProps } from 'react-flatpickr';

import clsx from 'clsx';
import dayjs from 'dayjs';
import en from 'dayjs/locale/en';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { UPDATE_DATE_PERIOD, UPDATE_PERIOD } from '@/utils/hooks/datepicker/actions';
// Context
import { useDatepicker } from '@/utils/hooks/datepicker/useDatepicker';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { DatepickerDateType } from '@/utils/hooks/datepicker/types';

import type { PeriodsVariantType } from '@/lib/datepicker/datePickerPeriodsConfig';
// Lib
import { periodsVariant } from '@/lib/datepicker/datePickerPeriodsConfig';

// Components
import Tooltip from '@/components/base/Tooltip';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

const getEndDate = (
    currentDate: Date | string,
    minDate: DateTimePickerProps['options']['minDate'],
    maxDate: DateTimePickerProps['options']['maxDate']
) => {
    if (maxDate && dayjs(maxDate).isBefore(dayjs(currentDate))) {
        // If there is a maxDate => check if the end date is greater than maxDate
        return maxDate;
    } else if (minDate && minDate > currentDate) {
        // If there is a minDate => check if the end date is lower than minDate
        return minDate;
    }
    return currentDate;
};

const DatepickerPeriods = () => {
    const { t, lang } = useTranslation('commons');
    // Context
    const { datepickerData, dispatch } = useDatepicker();
    const minDate = datepickerData?.options?.minDate;
    const maxDate = datepickerData?.options?.maxDate;

    dayjs.locale({
        ...en,
        weekStart: datepickerData?.firstDayOfWeek ?? 1
    });

    const currentDayTz = useMemo(() => {
        return maxDate ? dayjs(maxDate) : dayjs().utc().tz(datepickerData.timezone);
    }, [datepickerData.timezone, maxDate]);

    const minDateTz = useMemo(() => {
        return minDate ? dayjs(minDate) : null;
    }, [minDate]);

    const periods = useMemo(() => {
        return {
            all_time: {
                id: 'all_time',
                label: t('datepicker.all_time'),
                dates: [
                    minDateTz
                        ? minDateTz.format()
                        : currentDayTz.subtract(29, 'day').startOf('day').format(),
                    currentDayTz.format()
                ]
            },
            previous_week: {
                id: 'previous_week',
                label: t('datepicker.previous_week'),
                dates: [
                    currentDayTz.subtract(1, 'week').startOf('week').format(),
                    currentDayTz.subtract(1, 'week').endOf('week').format()
                ]
            },
            this_week: {
                id: 'this_week',
                label: t('datepicker.this_week'),
                dates: [currentDayTz.startOf('week').format(), currentDayTz.format()]
            },
            last_7_days: {
                id: 'last_7_days',
                label: t('datepicker.last_7_days'),
                dates: [
                    currentDayTz.startOf('day').subtract(6, 'days').format(),
                    currentDayTz.endOf('day').format()
                ]
            },
            last_day: {
                id: 'last_day',
                label: t('datepicker.last_day'),
                dates: [
                    currentDayTz.startOf('day').subtract(1, 'day').format(),
                    currentDayTz.subtract(1, 'day').endOf('day').format()
                ]
            },
            previous_month: {
                id: 'previous_month',
                label: t('datepicker.previous_month'),
                active: true,
                dates: [
                    currentDayTz.subtract(1, 'month').startOf('month').format(),
                    currentDayTz.subtract(1, 'month').endOf('month').format()
                ]
            },
            this_month: {
                id: 'this_month',
                label: t('datepicker.this_month'),
                active: true,
                dates: [currentDayTz.startOf('month').format(), currentDayTz.format()]
            },
            last_30_days: {
                id: 'last_30_days',
                label: t('datepicker.last_30_days'),
                dates: [
                    currentDayTz.subtract(29, 'day').startOf('day').format(),
                    currentDayTz.endOf('day').format()
                ]
            },
            last_3_months: {
                id: 'last_3_months',
                label: t('datepicker.last_3_months'),
                dates: [
                    currentDayTz.subtract(3, 'month').startOf('day').format(),
                    currentDayTz.endOf('day').format()
                ]
            },
            this_year: {
                id: 'this_year',
                label: t('datepicker.this_year'),
                dates: [currentDayTz.startOf('year').format(), currentDayTz.format()]
            },
            previous_year: {
                id: 'previous_year',
                label: t('datepicker.previous_year'),
                dates: [
                    currentDayTz.subtract(1, 'year').startOf('year').format(),
                    currentDayTz.subtract(1, 'year').endOf('year').format()
                ]
            },
            this_quarter: {
                id: 'this_quarter',
                label: t('datepicker.this_quarter'),
                dates: [currentDayTz.startOf('quarter').format(), currentDayTz.format()]
            },
            previous_quarter: {
                id: 'previous_quarter',
                label: t('datepicker.previous_quarter'),
                dates: [
                    currentDayTz.subtract(1, 'quarter').startOf('quarter').format(),
                    currentDayTz.subtract(1, 'quarter').endOf('quarter').format()
                ]
            },
            custom: {
                id: 'custom',
                label: t('datepicker.custom')
            }
        } as const;
    }, [currentDayTz, lang, minDateTz]);

    const periodsList = useCallback(() => {
        return periodsVariant[datepickerData.periods].map((item) => {
            return periods[item] ? periods[item] : null;
        }) as {
            [TKey in keyof PeriodsVariantType]: (typeof periods)[PeriodsVariantType[TKey][number]][];
        }[keyof PeriodsVariantType];
    }, [datepickerData.periods, periods]);

    useEffect(() => {
        let currentPeriod = datepickerData.periodSelected;
        if (currentPeriod in periods) {
            let datesSelected = [] as DatepickerDateType;
            if (currentPeriod === 'custom') {
                // If there is a minDate => check if the start date is lower than minDate
                const startDate =
                    minDate && minDate > datepickerData.date[0] ? minDate : datepickerData.date[0];

                const endDate = getEndDate(datepickerData.date[1], minDate, maxDate);

                // Change dates selected
                datesSelected = [startDate, endDate];
            } else {
                // Check if current period is disabled and change to last 30 days
                const isCurrentPeriodDisabled =
                    minDate && minDate > periods[currentPeriod].dates[0];
                currentPeriod = isCurrentPeriodDisabled ? 'last_30_days' : currentPeriod;
                // Change dates selected
                const rangeDates = periods[currentPeriod].dates;
                const endDate = getEndDate(rangeDates[1], minDate, maxDate);
                datesSelected = [rangeDates[0], endDate];
            }
            dispatch({
                type: UPDATE_DATE_PERIOD,
                payload: {
                    date: datesSelected,
                    period: currentPeriod
                }
            });
        }
    }, [datepickerData.periodSelected, datepickerData.options.minDate, dispatch, periods]);

    const displayList = () => {
        return periodsList().map((period: (typeof periods)[keyof typeof periods], key: number) => {
            const isActive = datepickerData.periodSelected === period.id;
            const isDisabled =
                datepickerData.options.minDate &&
                'dates' in period &&
                datepickerData.options.minDate > period.dates[0];

            return (
                <Tooltip
                    key={key}
                    position="top-start"
                    content={isDisabled ? t('datepicker.no_data_period') : null}>
                    <li>
                        <button
                            type="button"
                            disabled={isDisabled}
                            onClick={() => {
                                if (!isActive) {
                                    dispatch({
                                        type: UPDATE_PERIOD,
                                        payload: period.id
                                    });
                                }
                            }}
                            className={clsx('relative w-full px-5 py-3 text-left ring-inset', {
                                'text-icoBlue': isActive && !isDisabled,
                                'cursor-default text-icoGray-400 dark:text-icoDarkMode-beton':
                                    isDisabled
                            })}>
                            {period.label}
                            <span
                                className={clsx('absolute right-0 top-0 h-full w-0.5 bg-icoBlue', {
                                    hidden: !isActive || isDisabled
                                })}
                            />
                        </button>
                    </li>
                </Tooltip>
            );
        });
    };

    return (
        <ul className="flex flex-1 flex-col whitespace-nowrap rounded-bl-md rounded-tl-md bg-icoGray-100 py-3 font-GSemiBold text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf">
            {displayList()}
        </ul>
    );
};

export default DatepickerPeriods;
